import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useSWR from 'swr';

import { callBackbonePostApi } from '../../services/backboneApiService';

import {
  SHRED_CHALLENGE_ID,
  SHRED_LEADERBOARD_ACTION,
  SHRED_REPLAY_STEP_MS,
} from './constants';
import { buildDemoResponse, getDemoScenario } from './fixture';
import {
  getClockSkewMs,
  getEffectiveStatus,
  getNextBoundaryMs,
  getRankChangeNotice,
  getRefreshIntervalMs,
  normalizeResponse,
} from './helpers';

const HTTP_OK = 200;
const DEMO_LATENCY_MS = 250;
const COUNT_UP_SETTLE_GRACE_MS = 150;

export const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );
  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event) => setReduced(event.matches);
    if (query.addEventListener) {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);
  return reduced;
};

// Server-corrected clock: skewMs is (server time - local time) measured when the data arrived.
export const useNow = (intervalMs = 1000, skewMs = 0) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now + skewMs;
};

// Re-renders only when the status actually flips, so the page body does not tick every second.
export const useEffectiveStatus = (challenge, skewMs = 0) => {
  const [status, setStatus] = useState(() => getEffectiveStatus(challenge, Date.now() + skewMs));
  useEffect(() => {
    const evaluate = () => setStatus(getEffectiveStatus(challenge, Date.now() + skewMs));
    evaluate();
    const timer = window.setInterval(evaluate, 1000);
    return () => window.clearInterval(timer);
  }, [challenge, skewMs]);
  return status;
};

const fetchLeaderboard = async ({ demo, demoMe, asOfDay, userId }) => {
  if (demo) {
    await new Promise((resolve) => window.setTimeout(resolve, DEMO_LATENCY_MS));
    const body = buildDemoResponse({ asOfDay, scenario: getDemoScenario(demo), me: demoMe || undefined });
    return { ...normalizeResponse(body), clockSkewMs: getClockSkewMs(body.generatedAtMs, Date.now()) };
  }
  const payload = { action: SHRED_LEADERBOARD_ACTION, challengeId: SHRED_CHALLENGE_ID };
  if (userId) payload.userId = userId;
  if (asOfDay) payload.asOfDay = asOfDay;
  const response = await callBackbonePostApi(payload, '');
  if (response?.status !== HTTP_OK || !response.data) {
    throw new Error('Shred leaderboard unavailable');
  }
  const receivedAtMs = Date.now();
  const body = normalizeResponse(response.data);
  return { ...body, clockSkewMs: getClockSkewMs(body.generatedAtMs, receivedAtMs) };
};

const NO_DATA_RETRY_MS = 30000;

// SWR owns caching + polling. Live and upcoming views poll every minute (SWR pauses while the tab is
// hidden), an ended view does not. If the server-corrected clock is ahead of the status the server
// reported (the countdown just hit zero), we refetch at that boundary and retry quickly until the
// server catches up. Previous data is kept while another day loads so the board animates between days.
export const useShredData = ({ demo, demoMe, asOfDay, userId }) => {
  const key = ['shred', demo || 'api', demoMe || '', asOfDay || 'now', userId || ''];
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    key,
    () => fetchLeaderboard({ demo, demoMe, asOfDay, userId }),
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
      errorRetryInterval: NO_DATA_RETRY_MS,
      onErrorRetry: (err, retryKey, config, revalidate, { retryCount }) => {
        window.setTimeout(() => revalidate({ retryCount }), NO_DATA_RETRY_MS);
      },
      refreshInterval: (latest) =>
        asOfDay ? 0 : getRefreshIntervalMs(latest, Date.now()) ?? NO_DATA_RETRY_MS,
    }
  );

  const boundaryAt = !asOfDay && data ? getNextBoundaryMs(data.challenge, data.clockSkewMs) : null;
  useEffect(() => {
    if (boundaryAt === null) return undefined;
    const delay = Math.max(0, boundaryAt - Date.now()) + 500;
    const timer = window.setTimeout(() => mutate(), delay);
    return () => window.clearTimeout(timer);
  }, [boundaryAt, mutate]);

  return { data: data || null, error, isLoading, isValidating, reload: () => mutate() };
};

// Toast when a live poll moves the signed-in member's rank. Never on first load, after switching
// member (login / logout / demo persona), or while a replay day is showing.
export const useRankToast = ({ data, me, userId, replayDay, isValidating, onNotice }) => {
  const previous = useRef(null);
  useEffect(() => {
    previous.current = null;
  }, [userId]);
  useEffect(() => {
    if (!data || replayDay || isValidating) return;
    const mine = me?.eligible ? me.entry || null : null;
    const identity = me?.id || '';
    const before = previous.current;
    if (before && before.identity === identity) {
      const message = getRankChangeNotice(before.entry, mine);
      if (message) onNotice(message);
    }
    previous.current = { identity, entry: mine };
  }, [data, me, replayDay, isValidating, onNotice]);
};

// Steps through contest days for the replay. `null` means "the real current view".
// maxDayRef holds the live view's day count; it is read when an action or step runs, not captured at
// render, so a seek right after the data arrives never sees a stale (zero) range.
export const useReplay = ({ maxDayRef, ready }) => {
  const [replayDay, setReplayDay] = useState(null);
  const [playing, setPlaying] = useState(false);

  const goToDay = useCallback(
    (day) => {
      setReplayDay(day >= maxDayRef.current ? null : Math.max(1, day));
    },
    [maxDayRef]
  );

  const play = useCallback(() => {
    setReplayDay((current) => (current === null ? 1 : current));
    setPlaying(true);
  }, []);

  const pause = useCallback(() => setPlaying(false), []);

  const reset = useCallback(() => {
    setPlaying(false);
    setReplayDay(null);
  }, []);

  useEffect(() => {
    if (!playing || !ready) {
      return undefined;
    }
    const maxDay = maxDayRef.current;
    const current = replayDay === null ? maxDay : replayDay;
    if (current >= maxDay) {
      setPlaying(false);
      setReplayDay(null);
      return undefined;
    }
    const timer = window.setTimeout(
      () => setReplayDay(current + 1 >= maxDay ? maxDay : current + 1),
      SHRED_REPLAY_STEP_MS
    );
    return () => window.clearTimeout(timer);
  }, [playing, ready, replayDay, maxDayRef]);

  // replayDay === maxDay would ask the API for "as of the last day", which equals live/final.
  const effectiveReplayDay = replayDay !== null && replayDay >= maxDayRef.current ? null : replayDay;

  return {
    asOfDay: effectiveReplayDay,
    isReplaying: effectiveReplayDay !== null || playing,
    playing,
    goToDay,
    play,
    pause,
    reset,
  };
};

export const useCountUp = (target, { duration = 800, disabled = false } = {}) => {
  const [value, setValue] = useState(target);
  const valueRef = useRef(target);
  useEffect(() => {
    if (disabled || !Number.isFinite(target) || !Number.isFinite(valueRef.current)) {
      valueRef.current = target;
      setValue(target);
      return undefined;
    }
    const from = valueRef.current;
    if (from === target) {
      return undefined;
    }
    let frame;
    const startedAt = performance.now();
    const tick = (nowTs) => {
      const progress = Math.min(1, (nowTs - startedAt) / duration);
      const eased = 1 - (1 - progress) ** 3;
      const next = from + (target - from) * eased;
      valueRef.current = next;
      setValue(next);
      if (progress < 1) {
        frame = window.requestAnimationFrame(tick);
      }
    };
    frame = window.requestAnimationFrame(tick);
    const settleTimer = window.setTimeout(() => {
      valueRef.current = target;
      setValue(target);
    }, duration + COUNT_UP_SETTLE_GRACE_MS);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
    };
  }, [target, duration, disabled]);
  return value;
};

// While the replay runs, a section never shrinks below the tallest it has been, starting from the
// live layout at the moment the replay begins, so the sections below it do not jump day to day.
export const useStableHeight = (active) => {
  const nodeRef = useRef(null);
  const liveHeightRef = useRef(0);
  const floorRef = useRef(0);

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }
    if (!active) {
      liveHeightRef.current = node.offsetHeight;
      floorRef.current = 0;
      node.style.minHeight = '';
      return;
    }
    floorRef.current = Math.max(floorRef.current, liveHeightRef.current, node.offsetHeight);
    node.style.minHeight = `${floorRef.current}px`;
  });

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const release = () => {
      liveHeightRef.current = 0;
      floorRef.current = 0;
      if (nodeRef.current) {
        nodeRef.current.style.minHeight = '';
      }
    };
    window.addEventListener('resize', release);
    return () => window.removeEventListener('resize', release);
  }, [active]);

  return nodeRef;
};

// FLIP list reordering: measure offsets before/after render, invert, then play. Positions are
// re-measured on every render so a layout shift above the list is not mistaken for a reorder; only a
// change of order (orderKey) animates.
export const useFlip = (orderKey, disabled) => {
  const nodes = useRef(new Map());
  const positions = useRef(new Map());
  const lastKey = useRef(orderKey);

  const register = useCallback(
    (id) => (node) => {
      if (node) nodes.current.set(id, node);
      else nodes.current.delete(id);
    },
    []
  );

  useLayoutEffect(() => {
    const orderChanged = lastKey.current !== orderKey;
    lastKey.current = orderKey;
    const next = new Map();
    nodes.current.forEach((node, id) => {
      const top = node.offsetTop;
      next.set(id, top);
      const previous = positions.current.get(id);
      if (orderChanged && !disabled && previous !== undefined && Math.abs(previous - top) > 1) {
        node.style.transition = 'none';
        node.style.transform = `translateY(${previous - top}px)`;
        // Force a reflow so the inverted position is committed before we animate to rest.
        // eslint-disable-next-line no-unused-expressions
        node.offsetHeight;
        node.style.transition = 'transform 650ms cubic-bezier(0.2, 0.8, 0.2, 1)';
        node.style.transform = '';
      }
    });
    positions.current = next;
  });

  return register;
};
